import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";
import { submitNetlifyForm } from "@/lib/api";

const RECOMMEND = {
  superyacht: [
    { name: "Furuno TZtouch3 · dual 16\"", note: "Redundant charting with radar & PBG overlay" },
    { name: "Furuno DRS Solid-State Radar", note: "Doppler target tracking, offshore to inlet" },
    { name: "Starlink Maritime", note: "Ship-wide high-speed internet, always on" },
    { name: "NMEA 2000 backbone", note: "Every sensor on one certified network" },
    { name: "Gyro-stabilized autopilot", note: "Hands-off, on-course passage-making" },
  ],
  sportfish: [
    { name: "Garmin GPSMAP 9000", note: "Ultra-bright tournament glass helm" },
    { name: "Furuno CHIRP DFF3-UHD", note: "Mark bait, structure & thermoclines" },
    { name: "Open-array radar", note: "Find birds & weather far offshore" },
    { name: "Starlink Maritime", note: "Stream & report live from the canyon" },
  ],
  console: [
    { name: "Simrad NSX / Garmin GPSMAP", note: "Waterproof glass helm, plug-and-play" },
    { name: "Digital dome radar", note: "Close-range collision avoidance" },
    { name: "VHF + AIS transponder", note: "See and be seen in traffic" },
    { name: "Fusion Apollo audio", note: "Fully marinized sound system" },
  ],
  runabout: [
    { name: "Garmin GPSMAP compact", note: "Clean helm that respects classic lines" },
    { name: "GPS + coastal charts", note: "Simple, confident navigation" },
    { name: "VHF radio", note: "Coastal safety net" },
    { name: "Fusion audio", note: "Day-boat sound, done right" },
  ],
  tender: [
    { name: "Garmin compact GPS", note: "Pocket-size navigation" },
    { name: "Handheld VHF", note: "Always in contact with the mothership" },
    { name: "AIS transponder", note: "Trackable from the yacht" },
  ],
};
RECOMMEND.yacht = RECOMMEND.superyacht;

export default function Configurator() {
  const { t, lang } = useApp();
  const [step, setStep] = useState(0);
  const [vessel, setVessel] = useState(null);
  const [prios, setPrios] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [done, setDone] = useState(false);

  const togglePrio = (p) => setPrios((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);
  const recs = (vessel && RECOMMEND[vessel]) || [];

  const submit = async () => {
    if (!form.name || !/^\S+@\S+\.\S+$/.test(form.email)) { toast.error(t.form.invalidEmail); return; }
    try {
      await submitNetlifyForm("configurator", {
        ...form,
        vessel_type: vessel,
        systems: [...prios, ...recs.map((r) => r.name)],
        source: "configurator",
      });
      setDone(true);
      toast.success(t.contact.success);
    } catch {
      toast.error("Network error — please try again");
    }
  };

  return (
    <section id="config" className="relative py-28 lg:py-40 bg-background" data-testid="config-section">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight max-w-2xl leading-[1.05]">{t.config.title}</h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 bg-card border border-border rounded-3xl p-8 md:p-12">
          {/* steps indicator */}
          <div className="flex items-center gap-4 mb-10">
            {t.config.steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-full grid place-items-center font-mono text-xs ${i <= step ? "bg-cyan text-white" : "border border-border text-muted-foreground"}`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <span className={`font-mono text-[10px] tracking-[0.2em] uppercase ${i === step ? "text-cyan" : "text-muted-foreground"}`}>{s}</span>
                {i < 2 && <span className="w-8 h-px bg-border" />}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="config-success">
                <div className="w-16 h-16 rounded-full bg-cyan/15 grid place-items-center mx-auto mb-6"><Check className="w-8 h-8 text-cyan" /></div>
                <h3 className="font-display text-3xl mb-3">{t.contact.success}</h3>
              </motion.div>
            ) : step === 0 ? (
              <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <p className="font-display text-2xl mb-6">{t.config.q1}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {t.boats.types.map((b) => (
                    <button key={b.id} onClick={() => { setVessel(b.id); setStep(1); }} data-testid={`config-vessel-${b.id}`}
                      className={`p-6 rounded-xl border text-left transition-all ${vessel === b.id ? "border-cyan bg-cyan/10" : "border-border hover:border-cyan"}`}>
                      <p className="font-display text-xl">{b.t}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : step === 1 ? (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <p className="font-display text-2xl mb-6">{t.config.q2}</p>
                <div className="flex flex-wrap gap-3 mb-8">
                  {t.config.priorities.map((p) => (
                    <button key={p} onClick={() => togglePrio(p)} data-testid={`config-prio-${p}`}
                      className={`font-mono text-xs uppercase tracking-wider px-5 py-3 rounded-full border transition-all ${prios.includes(p) ? "bg-cyan text-white border-cyan" : "border-border hover:border-cyan"}`}>
                      {p}
                    </button>
                  ))}
                </div>
                {recs.length > 0 && (
                  <div className="border-t border-border pt-6">
                    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-brass mb-1">{t.config.recommend}</p>
                    <p className="text-xs text-muted-foreground mb-4">{t.config.recommendNote}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {recs.map((r) => (
                        <div key={r.name} className="flex items-start gap-3 rounded-lg border border-border p-3" data-testid="config-rec">
                          <Check className="w-4 h-4 text-cyan mt-0.5 shrink-0" />
                          <div>
                            <p className="font-mono text-sm text-foreground leading-tight">{r.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{r.note}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-between mt-8">
                  <button onClick={() => setStep(0)} className="font-mono text-xs uppercase tracking-wider flex items-center gap-2 text-muted-foreground hover:text-cyan" data-testid="config-back-1"><ArrowLeft className="w-4 h-4" />{t.config.back}</button>
                  <button onClick={() => setStep(2)} className="font-mono text-xs uppercase tracking-wider flex items-center gap-2 bg-cyan text-white px-6 py-3 rounded-full" data-testid="config-next-1">{t.config.next}<ArrowRight className="w-4 h-4" /></button>
                </div>
              </motion.div>
            ) : (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <p className="font-display text-2xl mb-6">{t.config.q3}</p>
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                  {["name", "email", "phone"].map((f) => (
                    <input key={f} type={f === "email" ? "email" : "text"} placeholder={t.contact[f]} value={form[f]}
                      onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`config-input-${f}`}
                      className="bg-transparent border-b border-border focus:border-cyan outline-none py-3 font-mono text-sm placeholder:text-muted-foreground transition-colors" />
                  ))}
                </div>
                <div className="flex justify-between">
                  <button onClick={() => setStep(1)} className="font-mono text-xs uppercase tracking-wider flex items-center gap-2 text-muted-foreground hover:text-cyan" data-testid="config-back-2"><ArrowLeft className="w-4 h-4" />{t.config.back}</button>
                  <button onClick={submit} className="font-mono text-xs uppercase tracking-wider bg-cyan text-white px-7 py-3 rounded-full hover:glow-cyan" data-testid="config-submit">{t.config.submit}</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
}
